import { useCallback, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { focusMainTransiently } from "../focus-main";

const CODES_WARNING_BLOCK = "--codes-warning-block";
const CODES_WARNING_PHONE = "(max-width: 1023px)";

function publishCodesWarningBlock(node: HTMLElement): void {
  const root = document.documentElement;
  const phone =
    typeof window.matchMedia === "function" ? window.matchMedia(CODES_WARNING_PHONE) : null;
  if (phone === null || !phone.matches) {
    root.style.removeProperty(CODES_WARNING_BLOCK);
    return;
  }
  const rect = node.getBoundingClientRect();
  if (rect.height === 0) {
    root.style.removeProperty(CODES_WARNING_BLOCK);
    return;
  }
  root.style.setProperty(CODES_WARNING_BLOCK, `${Math.ceil(window.innerHeight - rect.top)}px`);
}

function clearCodesWarningBlock(): void {
  document.documentElement.style.removeProperty(CODES_WARNING_BLOCK);
}

type RecoveryCodesAtRiskBannerProps = { announce: boolean } & (
  | {
      state: "unchecked";
      checking: boolean;
      retryFailed: boolean;
      waitingForNetwork: boolean;
      announceWaiting: boolean;
      sessionEnded: boolean;
      onRetry: () => void;
      onSignIn: () => void;
    }
  | {
      state: "loaded";
      onGoToAccount: () => void;
      onRelease: () => void;
      hideRelease?: boolean;
      unseen?: boolean;
    }
);

export function RecoveryCodesAtRiskBanner(props: RecoveryCodesAtRiskBannerProps) {
  const { t } = useTranslation();
  const messageId = useId();
  const waitingId = useId();
  const releaseHintId = useId();
  const goToAccountRef = useRef<HTMLButtonElement>(null);
  const retryHadFocusRef = useRef(false);
  const releaseHadFocusRef = useRef(false);
  const hideRelease = props.state === "loaded" && props.hideRelease === true;
  const sessionEnded = props.state === "unchecked" && props.sessionEnded;
  const waitingForNetwork = props.state === "unchecked" && props.waitingForNetwork && !sessionEnded;
  const announceSessionEnded = sessionEnded && props.state === "unchecked" && props.retryFailed;
  const messageKey = announceSessionEnded
    ? "session-ended-announced"
    : props.announce
      ? "announced"
      : "quiet";

  const rootRef = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    const publish = () => publishCodesWarningBlock(node);
    publish();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(publish) : null;
    observer?.observe(node);
    const phone =
      typeof window.matchMedia === "function" ? window.matchMedia(CODES_WARNING_PHONE) : null;
    if (typeof phone?.addEventListener === "function") phone.addEventListener("change", publish);
    window.addEventListener("resize", publish);
    return () => {
      observer?.disconnect();
      if (typeof phone?.removeEventListener === "function")
        phone.removeEventListener("change", publish);
      window.removeEventListener("resize", publish);
      clearCodesWarningBlock();
      if (node.contains(document.activeElement)) focusMainTransiently();
    };
  }, []);

  useEffect(() => {
    if (props.state !== "loaded" || !retryHadFocusRef.current) return;
    retryHadFocusRef.current = false;
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    goToAccountRef.current?.focus();
  }, [props.state]);

  useEffect(() => {
    if (!hideRelease || !releaseHadFocusRef.current) return;
    releaseHadFocusRef.current = false;
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    goToAccountRef.current?.focus();
  }, [hideRelease]);

  return (
    <div
      ref={rootRef}
      role="region"
      aria-label={t("account.passkeys.recoveryCodes")}
      className="fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border border-danger/30 bg-surface p-4 shadow-lg"
    >
      <p
        key={messageKey}
        id={messageId}
        role={messageKey === "quiet" ? undefined : "alert"}
        className="flex items-start gap-2 text-xs text-heading"
      >
        <ShieldAlert size={16} className="mt-0.5 shrink-0 text-danger" />
        <span>
          {props.state === "loaded"
            ? props.unseen === true
              ? t("account.codesAtRisk.bodyUnseen")
              : t("account.codesAtRisk.body")
            : sessionEnded
              ? t("account.codesAtRisk.bodySessionEnded")
              : t("account.codesAtRisk.bodyUnchecked")}
        </span>
      </p>
      {props.state === "unchecked" && waitingForNetwork && (
        <p
          key={props.announceWaiting ? "waiting-announced" : "waiting-quiet"}
          id={waitingId}
          role={props.announceWaiting ? "alert" : undefined}
          className="mt-2 text-xs text-muted"
        >
          {t("common.waitingForNetwork")}
        </p>
      )}
      {props.state === "unchecked" && props.retryFailed && !props.checking && !sessionEnded && (
        <p role="alert" className="mt-2 text-xs text-muted">
          {t("account.codesAtRisk.retryFailed")}
        </p>
      )}
      {props.state === "loaded" ? (
        <div className="mt-3 flex flex-col items-end gap-2">
          <button
            key="go-to-account"
            ref={goToAccountRef}
            type="button"
            onClick={props.onGoToAccount}
            aria-describedby={messageId}
            className="text-xs font-semibold text-indigo transition-colors hover:text-indigo-bright"
          >
            {t("account.codesAtRisk.goToAccount")}
          </button>
          {props.hideRelease ? null : (
            <div className="flex flex-col items-end">
              <button
                type="button"
                ref={(node) => {
                  if (node === null) return;
                  return () => {
                    releaseHadFocusRef.current = document.activeElement === node;
                  };
                }}
                onClick={props.onRelease}
                aria-describedby={releaseHintId}
                className="text-xs font-medium text-muted transition-colors hover:text-heading"
              >
                {t("account.codesAtRisk.saved")}
              </button>
              <p id={releaseHintId} className="text-right text-xs text-muted">
                {t("account.codesAtRisk.savedConsequence")}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 flex justify-end">
          <button
            key="retry"
            ref={(node) => {
              if (node === null) return;
              return () => {
                retryHadFocusRef.current = document.activeElement === node;
              };
            }}
            type="button"
            onClick={() => {
              if (sessionEnded) props.onSignIn();
              else if (!props.checking) props.onRetry();
            }}
            aria-describedby={waitingForNetwork ? `${messageId} ${waitingId}` : messageId}
            aria-disabled={props.checking || undefined}
            aria-busy={(props.checking && !waitingForNetwork) || undefined}
            className="text-xs font-semibold text-indigo transition-colors hover:text-indigo-bright aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {sessionEnded ? t("account.staleSession.action") : t("common.retry")}
          </button>
        </div>
      )}
    </div>
  );
}
